import { FotoDoProduto } from './FotoDoProduto';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu, Falha } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  EXPLICACAO_DO_RESULTADO,
  ROTULO_DO_DESFECHO,
  ROTULO_DA_ETIQUETA,
  ROTULO_DO_ESTADO,
  ROTULO_DO_LOCAL,
  ROTULO_DO_RESULTADO,
  anotar,
  concluir,
  dataCurta,
  ha,
  hora,
  iniciarAnalise,
  lerCaso,
  lerEtapas,
  lerFila,
  numeroDoCaso,
  reabrir,
  registrarRecontagem,
} from '../../auditoria';
import type {
  AuditoriaDoCaso,
  CasoCompleto,
  CasoDaFila,
  DesfechoDoCaso,
  EtapasDoCaso,
  EventoDoCaso,
  FilaDoAuditor,
  LocalDaRecontagem,
  ResultadoDaAuditoria,
  ResultadoDaConclusao,
} from '../../auditoria';
import { ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import type { Escrita } from '../../giro';
import { enviarFoto } from '../../vendas';
import { agoraCarimbo, carimbarFoto, type FonteDaFoto } from '../balconista/foto';
import { CameraDaFoto } from '../balconista/CameraDaFoto';
import './caso.css';
import './etapas.css';
import { ConcluirSemEtapas, ConferenciaSimples } from './PaineisDaEtapa';
import {
  consultaDe,
  enderecoDoCaso,
  lerVolta,
  resumoDoContexto,
  voltaAtual,
} from './contexto-da-fila';

/**
 * TELA 23 — CASO E EVIDÊNCIAS. "A conclusão fica no fim da investigação."
 *
 * A ordem da tela é a do design: grandezas do caso (valores do momento, não recalculados), linha
 * do tempo separando ação humana, sistema e dado externo, movimentos entre os registros (do
 * servidor), evidências, próximo passo — e só então o resultado.
 *
 *   - A recontagem é EVIDÊNCIA NOVA: o valor da balconista nunca é alterado.
 *   - "Erro de contagem confirmado" passa por confirmação; o servidor exige recontagem e as vendas
 *     do HOS cobertas até ela (409 com a explicação) e rebaixa para inconclusivo quando houve
 *     movimento no período — a tela mostra o rebaixamento, nunca esconde.
 *   - Concluído é leitura. Reabrir é do administrador, com motivo.
 *   - A tela não afirma causalidade: mostra os eventos na ordem em que aconteceram.
 *
 * Quem não audita (a balconista vendo o PRÓPRIO caso) vê tudo em modo leitura: sem ações e sem
 * a fila lateral. Esconder botão é conveniência; quem recusa é o servidor.
 */

type Modo = 'ver' | 'recontar' | 'observar' | 'encaminhar' | 'concluir';
type Aviso =
  | { readonly tipo: 'conflito'; readonly texto: string }
  | { readonly tipo: 'incerta'; readonly titulo: string }
  | { readonly tipo: 'recusada'; readonly titulo: string; readonly texto: string }
  | { readonly tipo: 'ok'; readonly titulo: string; readonly texto?: string };

const ORDEM_DOS_RESULTADOS: readonly ResultadoDaAuditoria[] = [
  'BALCONISTA_CORRETO',
  'ERRO_BALCONISTA_CONFIRMADO',
  'INCONCLUSIVO',
  'PROBLEMA_DADOS',
  'PRODUTO_MUDOU_LOCAL',
];
/** No cartão de escolha o erro ganha o nome inteiro, factual: é a contagem, não a pessoa. */
const TITULO_DA_OPCAO: Readonly<Record<ResultadoDaAuditoria, string>> = {
  ...ROTULO_DO_RESULTADO,
  ERRO_BALCONISTA_CONFIRMADO: 'Erro de contagem confirmado',
};
const TOM_DO_RESULTADO: Readonly<Record<ResultadoDaAuditoria, string>> = {
  BALCONISTA_CORRETO: 'ok',
  ERRO_BALCONISTA_CONFIRMADO: 'erro',
  INCONCLUSIVO: 'ambar',
  PROBLEMA_DADOS: 'neutro',
  PRODUTO_MUDOU_LOCAL: 'neutro',
};
const DESTINOS = [
  { id: 'ADMIN', rotulo: 'Administração' },
  { id: 'DADOS', rotulo: 'Dados / integração' },
  { id: 'RECONTAGEM', rotulo: 'Recontagem na loja' },
] as const;
type Destino = (typeof DESTINOS)[number]['id'];

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

/** "−5" com o sinal de menos tipográfico; positivo ganha "+". */
const sinal = (n: number) => (n < 0 ? `−${String(-n)}` : n > 0 ? `+${String(n)}` : '0');
const quando = (iso: string) => `${dataCurta(iso)} às ${hora(iso)}`;
/** "2026-09-22" → "22/09/2026". */
const diaBr = (dia: string) => dia.split('-').reverse().join('/');
const primeiro = (nome: string | null) => (nome ?? '').split(' ')[0] ?? '';
const iniciais = (nome: string | null) =>
  (nome ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
const unidades = (n: number) => `${String(n)} ${n === 1 ? 'unidade' : 'unidades'}`;
const faixaDo = (c: CasoDaFila) =>
  [c.setor, `B${String(c.bloco)}`, c.faixa].filter(Boolean).join(' · ');

function useEstreito(limite = 699) {
  const consulta = `(max-width: ${String(limite)}px)`;
  const [estreito, setEstreito] = useState(() => window.matchMedia(consulta).matches);
  useEffect(() => {
    const m = window.matchMedia(consulta);
    const mudou = () => setEstreito(m.matches);
    m.addEventListener('change', mudou);
    return () => m.removeEventListener('change', mudou);
  }, [consulta]);
  return estreito;
}

/* ================================================================== *
 * A tela
 * ================================================================== */

export function CasoDeDivergencia({
  sessao,
  eu,
  id,
}: {
  sessao: Sessao;
  eu: Eu | null;
  id: string;
}) {
  const analisa = podeFazer(eu, 'giro.auditar') || podeFazer(eu, 'auditoria.analisar');
  // Quem vê todos os casos (gestão) volta para a fila; o balconista, para os casos dele.
  // O caso aberto a partir da Central traz o endereço da fila (`volta`): período, filtros e
  // ordem. A fila lateral, o anterior/próximo e o voltar usam ESSA fila — nunca a fila inteira.
  const [volta] = useState(voltaAtual);
  const contexto = lerVolta(volta);
  const voltarPara =
    contexto && volta
      ? volta
      : analisa || podeFazer(eu, 'auditoria.ver_todos') || podeFazer(eu, 'giro.administrar')
        ? '#/divergencias'
        : '#/minhas-auditorias';
  const reabre = podeFazer(eu, 'auditoria.reabrir');
  const estreito = useEstreito();

  const [caso, setCaso] = useState<CasoCompleto | null>(null);
  /** Divergências 3.0: as etapas do caso; `ativo` falso = a tela antiga, sem mudança. */
  const [etapas, setEtapas] = useState<EtapasDoCaso | null>(null);
  /** Concluir antes da etapa de ajuste (administrador, com motivo). */
  const [pular, setPular] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [fila, setFila] = useState<FilaDoAuditor | null>(null);
  const [modo, setModo] = useState<Modo>(() =>
    /[?&]modo=recontar\b/.test(window.location.hash) ? 'recontar' : 'ver',
  );
  /** Posição do caso na fila do contexto — sobrevive a ele sair da lista ao ser concluído. */
  const ultimaPosicao = useRef(-1);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [fechamento, setFechamento] = useState<ResultadoDaConclusao | null>(null);
  const iniciou = useRef(false);

  const carregar = useCallback(async () => {
    const [r, e] = await Promise.all([lerCaso(id), lerEtapas(id)]);
    // Sem as etapas (erro ou rota antiga) a tela segue no fluxo antigo.
    setEtapas(e.ok ? e.dados : null);
    if (r.ok) {
      setCaso(r.dados);
      setFalha(null);
    } else setFalha(r.falha);
    return r.ok ? r.dados : null;
  }, [id]);
  // Sem a fila da Central (caso aberto por Minhas auditorias, Qualidade, Bloqueados, link
  // direto): a lateral mostra os abertos do MESMO setor do caso — nunca todos os setores
  // misturados (Carlos, 08/10: "filtrei genéricos e a fila trouxe produto de outro filtro").
  const setorDoCaso = caso?.setorId ?? null;
  const confere =
    podeFazer(eu, 'divergencia.conferir_prateleira') ||
    podeFazer(eu, 'divergencia.conferir_estoque') ||
    podeFazer(eu, 'divergencia.decidir');
  const carregarFila = useCallback(() => {
    if (!analisa && !confere) return;
    if (!contexto && !setorDoCaso) return;
    void lerFila(
      contexto
        ? consultaDe(contexto.aba, contexto.filtros)
        : setorDoCaso
          ? { setor: setorDoCaso }
          : {},
    ).then((r) => {
      if (r.ok) setFila(r.dados);
    });
    // `contexto` é derivado de `volta`, que não muda durante a vida da tela.
  }, [analisa, confere, volta, setorDoCaso]);

  useEffect(() => {
    void carregar().then((c) => {
      // Pegar o caso para analisar: idempotente no servidor. Só quem analisa, só caso aberto.
      if (!c || !analisa || iniciou.current) return;
      if (c.estado !== 'NOVA' && c.estado !== 'REABERTA') return;
      iniciou.current = true;
      void iniciarAnalise(sessao, id, c.versao).then((r) => {
        if (r.tipo === 'confirmada') void carregar();
      });
    });
  }, [carregar, analisa, sessao, id]);
  useEffect(carregarFila, [carregarFila]);
  // Em tempo real (dono, 13/10): enquanto o caso está aberto nas 3 etapas, a contagem de quem está
  // na prateleira ou no estoque aparece para todo mundo sem recarregar. Só com a tela visível.
  const aoVivo = etapas?.ativo === true && caso !== null && caso.estado !== 'CONCLUIDA';
  useEffect(() => {
    if (!aoVivo) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void carregar();
    }, 5_000);
    return () => window.clearInterval(t);
  }, [aoVivo, carregar]);

  /** Os três desfechos de toda escrita. Incerto nunca aparece como recusa. */
  const tratar = <T,>(r: Escrita<T>, oQue: string, todo409 = false): T | null => {
    if (r.tipo === 'confirmada') return r.dados;
    if (r.tipo === 'incerta') setAviso({ tipo: 'incerta', titulo: oQue });
    // Nas etapas, todo 409 é "o caso mudou": recarrega e pede conferência antes de repetir.
    else if (r.status === 409 && (todo409 || /outra pessoa|outro usu/i.test(r.mensagem))) {
      setAviso({ tipo: 'conflito', texto: r.mensagem });
      void carregar();
    } else setAviso({ tipo: 'recusada', titulo: `${oQue} não foi aceita`, texto: r.mensagem });
    return null;
  };
  const [escolhaInicial, setEscolhaInicial] = useState<ResultadoDaAuditoria | null>(null);
  /** No computador o formulário nasce abaixo da dobra: leva a tela até ele. */
  const irAoFormulario = () =>
    window.setTimeout(
      () =>
        document.querySelector('.cz-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      60,
    );
  const trocarModo = (m: Modo) => {
    setAviso(null);
    if (m !== 'concluir') {
      setEscolhaInicial(null);
      setPular(false);
    }
    setModo(m);
    if (estreito) window.scrollTo({ top: 0 });
  };

  if (falha?.tipo === 'sem-acesso') return <SemAcesso area="A auditoria do Giro" />;
  if (falha)
    return (
      <section className="cz cz-so">
        <ErroDeArea
          oQue={falha.tipo === 'offline' ? 'o caso (sem conexão)' : 'o caso'}
          aoTentar={() => void carregar()}
        />
      </section>
    );
  if (!caso)
    return (
      <section className="cz cz-so" aria-busy="true">
        <div className="cz-carregando">Carregando o caso…</div>
      </section>
    );

  const aberto = caso.estado !== 'CONCLUIDA';
  const age = analisa && aberto;
  // Com contexto: a lista é exatamente a da Central (mesma ordem); sem contexto, os abertos.
  const abertos = (fila?.casos ?? []).filter(
    (c) => c.estado !== 'CONCLUIDA' || (contexto !== null && c.id === caso.id),
  );
  const lista = contexto ? (fila?.casos ?? []) : abertos;
  const posicao = lista.findIndex((c) => c.id === caso.id);
  if (posicao >= 0) ultimaPosicao.current = posicao;
  const base = posicao >= 0 ? posicao : ultimaPosicao.current;
  const outros = abertos.filter((c) => c.id !== caso.id);
  // Próximo = o próximo ABERTO depois deste na mesma fila (dá a volta no fim).
  const depois = [...lista.slice(base + 1), ...lista.slice(0, Math.max(0, base))];
  const proximo = depois.find((c) => c.id !== caso.id && c.estado !== 'CONCLUIDA') ?? null;
  const anterior =
    base > 0 ? ([...lista.slice(0, base)].reverse().find((c) => c.id !== caso.id) ?? null) : null;
  const linkDe = (id: string) => enderecoDoCaso(id, contexto ? volta : null);

  /** Fluxo de três etapas ligado e caso ainda aberto. */
  const ativo = etapas?.ativo === true && aberto;
  const decide = podeFazer(eu, 'divergencia.decidir');
  const props: PropsDoCaso = {
    etapas,
    pular,
    caso,
    sessao,
    eu,
    estreito,
    aviso,
    setAviso,
    tratar,
    carregar: async () => {
      await carregar();
      carregarFila();
    },
    trocarModo,
    reabre,
    escolhaInicial,
    escolher: (r: ResultadoDaAuditoria) => {
      setAviso(null);
      setEscolhaInicial(r);
      setModo('concluir');
      if (estreito) window.scrollTo({ top: 0 });
      else irAoFormulario();
    },
    irAoFormulario,
  };

  const painelDaEtapa =
    ativo && etapas && modo === 'ver' ? (
      <>
        <ConferenciaSimples
          caso={caso}
          etapas={etapas}
          sessao={sessao}
          eu={eu}
          setAviso={setAviso}
          tratar={tratar}
          carregar={props.carregar}
          aoConcluir={setFechamento}
        />
        {decide && analisa && etapas.etapa !== 'AJUSTE' && (
          <ConcluirSemEtapas
            aoEscolher={() => {
              setAviso(null);
              setPular(true);
              setModo('concluir');
              if (estreito) window.scrollTo({ top: 0 });
              else irAoFormulario();
            }}
          />
        )}
      </>
    ) : null;

  /* ---------- Celular: um formulário por vez, em tela cheia ---------- */
  if (estreito && age && modo !== 'ver') {
    const titulo =
      modo === 'recontar'
        ? 'Recontagem'
        : modo === 'concluir'
          ? 'Concluir auditoria'
          : modo === 'observar'
            ? 'Observação'
            : 'Encaminhar caso';
    return (
      <section className="cz cz-celular">
        <header className="cz-cel-topo">
          <button
            type="button"
            className="cz-voltar"
            onClick={() => trocarModo('ver')}
            aria-label="Voltar ao caso"
          >
            <Icone nome="arrow-l" tamanho={18} traco={1.7} />
          </button>
          <div className="cz-cel-titulo">
            <b>{titulo}</b>
            <span className="cz-mono">caso {numeroDoCaso(caso.numero)}</span>
          </div>
        </header>
        <div className="cz-cel-corpo">
          <div>
            <h1 className="cz-cel-produto">{caso.produto}</h1>
            <div className="cz-mono cz-sub">
              {[caso.codigo, `${caso.setor} B${String(caso.bloco)}`].filter(Boolean).join(' · ')}
            </div>
          </div>
          <Formulario modo={modo} {...props} aoConcluir={setFechamento} />
        </div>
      </section>
    );
  }

  const cabecalho = estreito ? (
    <>
      <header className="cz-cel-topo">
        <a className="cz-voltar" href={voltarPara} aria-label="Voltar">
          <Icone nome="arrow-l" tamanho={18} traco={1.7} />
        </a>
        <div className="cz-cel-titulo">
          <b className="cz-mono">{numeroDoCaso(caso.numero)}</b>
          <span>
            {posicao >= 0
              ? `${String(posicao + 1)} de ${String(lista.length)} na fila`
              : ROTULO_DO_ESTADO[caso.estado]}
          </span>
        </div>
        <span className={`cz-selo cz-selo-${aberto ? 'escuro' : 'ok'}`}>
          {ROTULO_DO_ESTADO[caso.estado].toUpperCase()}
        </span>
      </header>
      <div className="cz-cel-cab">
        <div className="cz-cab-produto">
          <FotoDoProduto url={caso.fotoProduto} nome={caso.produto} tamanho={60} />
          <div>
            <h1 className="cz-cel-produto">{caso.produto}</h1>
            <div className="cz-fabricante">{caso.fabricante ?? 'Laboratório não informado'}</div>
          </div>
        </div>
        <div className="cz-mono cz-sub">{faixaDo(caso)}</div>
        <div className="cz-cel-autoria">
          Item conferido por {caso.autoria ?? 'pessoa não identificada'} · {caso.dia.slice(8, 10)}/
          {caso.dia.slice(5, 7)}
        </div>
        {caso.resultado && (
          <span className={`cz-selo cz-selo-res-${TOM_DO_RESULTADO[caso.resultado]}`}>
            RESULTADO · {ROTULO_DO_RESULTADO[caso.resultado].toUpperCase()}
          </span>
        )}
      </div>
    </>
  ) : (
    <Cabecalho caso={caso} />
  );

  const avisoGeral =
    aviso && (modo === 'ver' || !age) ? <AvisoDaEscrita {...props} aviso={aviso} /> : null;

  const direita = (
    <>
      {!estreito && avisoGeral}
      {!estreito && painelDaEtapa}
      {age && !estreito && modo === 'ver' && !ativo && <Copiloto {...props} />}
      {fechamento && (
        <Fechamento
          r={fechamento}
          proximo={proximo}
          restantes={outros.length}
          concluido={!aberto}
          linkDe={linkDe}
          voltarPara={voltarPara}
        />
      )}
      {!aberto && (
        <Conclusao
          caso={caso}
          seguir={analisa ? { proximo, restantes: outros.length, linkDe, voltarPara } : undefined}
        />
      )}
      {!aberto && reabre && <Reabertura {...props} />}
      {caso.estado === 'REABERTA' && <Conclusao caso={caso} anterior />}
      {!estreito && <Movimentos caso={caso} />}
      {caso.recontagensFeitas.length > 0 && <DuasContagens caso={caso} />}
      {!estreito && <Evidencias caso={caso} />}
      {age && !estreito && (!ativo || modo !== 'ver') && (
        <Formulario modo={modo} {...props} aoConcluir={setFechamento} />
      )}
      {(caso.auditorias.length > 0 ||
        caso.notas.length > 0 ||
        caso.recontagensFeitas.length > 0) && <Historico caso={caso} />}
    </>
  );

  return (
    <section className={`cz${analisa && !estreito ? ' cz-com-fila' : ''}`}>
      {analisa && !estreito && (
        <FilaLateral
          setorDoCaso={caso.setor}
          fila={fila}
          atual={caso.id}
          lista={lista}
          contexto={contexto}
          voltarPara={voltarPara}
          linkDe={linkDe}
          posicao={posicao}
          anterior={anterior}
          proximo={proximo}
        />
      )}
      <div className="cz-principal">
        {cabecalho}
        {estreito ? (
          <div className="cz-cel-corpo">
            {avisoGeral}
            {painelDaEtapa}
            {age && !ativo && <Copiloto {...props} />}
            {!ativo && <AvisosDoProduto caso={caso} />}
            <Grandezas caso={caso} compacta />
            <LinhaDoTempo caso={caso} dobravel />
            <Dobra icone="cam" titulo="Evidências" extra={contagemDeFotos(caso)}>
              <Evidencias caso={caso} solto />
            </Dobra>
            <Dobra icone="db" titulo="Movimentos e dados técnicos">
              <Grandezas caso={caso} soRodape />
              <Movimentos caso={caso} solto />
            </Dobra>
            {direita}
            {age && (
              <div className="cz-cel-mais">
                <button type="button" onClick={() => trocarModo('observar')}>
                  Registrar observação
                </button>
                <span aria-hidden="true">·</span>
                <button type="button" onClick={() => trocarModo('encaminhar')}>
                  Encaminhar caso
                </button>
              </div>
            )}
            {age && !ativo && (
              <div className="cz-cel-rodape">
                <button
                  type="button"
                  className="cz-botao cz-botao-contorno cz-botao-g"
                  onClick={() => trocarModo('recontar')}
                >
                  <Icone nome="scan" tamanho={17} traco={1.7} /> Fazer recontagem
                </button>
                <button
                  type="button"
                  className="cz-botao cz-botao-primario cz-botao-g"
                  onClick={() => trocarModo('concluir')}
                >
                  Concluir auditoria
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="cz-grade">
            <div className="cz-coluna">
              {!ativo && <AvisosDoProduto caso={caso} />}
              <Grandezas caso={caso} />
              <LinhaDoTempo caso={caso} />
            </div>
            <div className="cz-coluna">{direita}</div>
          </div>
        )}
      </div>
    </section>
  );
}

interface PropsDoCaso {
  caso: CasoCompleto;
  etapas: EtapasDoCaso | null;
  /** Conclusão sem as etapas (motivo obrigatório, enviada com pularEtapas). */
  pular: boolean;
  sessao: Sessao;
  eu: Eu | null;
  estreito: boolean;
  aviso: Aviso | null;
  setAviso: (a: Aviso | null) => void;
  tratar: <T>(r: Escrita<T>, oQue: string, todo409?: boolean) => T | null;
  carregar: () => Promise<void>;
  trocarModo: (m: Modo) => void;
  reabre: boolean;
  /** Resultado que o Copiloto deixou marcado ao abrir o Concluir. */
  escolhaInicial: ResultadoDaAuditoria | null;
  /** Abre o Concluir com o resultado já marcado. */
  escolher: (r: ResultadoDaAuditoria) => void;
  irAoFormulario: () => void;
}

/* ================================================================== *
 * Peças
 * ================================================================== */

function Cartao({
  titulo,
  extra,
  rodape,
  children,
  forte,
  className,
}: {
  titulo: ReactNode;
  extra?: ReactNode;
  rodape?: ReactNode;
  children: ReactNode;
  forte?: boolean;
  className?: string;
}) {
  return (
    <div className={`cz-cartao${forte ? ' cz-cartao-forte' : ''} ${className ?? ''}`}>
      <div className="cz-cartao-topo">
        <h2>{titulo}</h2>
        {extra}
      </div>
      {children}
      {rodape && <div className="cz-cartao-rodape">{rodape}</div>}
    </div>
  );
}

function Dobra({
  icone,
  titulo,
  extra,
  aberta,
  children,
}: {
  icone: NomeDoIcone;
  titulo: string;
  extra?: string;
  aberta?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="cz-dobra" open={aberta}>
      <summary>
        <Icone nome={icone} tamanho={16} traco={1.6} />
        <b>{titulo}</b>
        {extra && <span>{extra}</span>}
        <span className="cz-dobra-seta">
          <Icone nome="chev-r" tamanho={15} traco={1.6} />
        </span>
      </summary>
      <div className="cz-dobra-corpo">{children}</div>
    </details>
  );
}

function contagemDeFotos(c: CasoCompleto) {
  return `${String(c.fotos.length)} ${c.fotos.length === 1 ? 'foto' : 'fotos'}`;
}

/* ---------- Fila lateral (desktop) ---------- */

function FilaLateral({
  fila,
  atual,
  lista,
  contexto,
  voltarPara,
  linkDe,
  posicao,
  anterior,
  proximo,
  setorDoCaso,
}: {
  fila: FilaDoAuditor | null;
  atual: string;
  setorDoCaso: string;
  lista: readonly CasoDaFila[];
  contexto: ReturnType<typeof lerVolta>;
  voltarPara: string;
  linkDe: (id: string) => string;
  posicao: number;
  anterior: CasoDaFila | null;
  proximo: CasoDaFila | null;
}) {
  const resumo = contexto
    ? resumoDoContexto(contexto.filtros, {
        setor: fila?.setores.find((s) => s.id === contexto.filtros.setor)?.nome,
        pessoa: fila?.pessoas.find((s) => s.id === contexto.filtros.pessoa)?.nome,
      })
    : null;
  const n = lista.length;
  return (
    <aside className="cz-fila" aria-label="Fila de divergências">
      <a className="cz-fila-topo" href={voltarPara}>
        <Icone nome="arrow-l" tamanho={18} traco={1.7} />
        <b>
          {resumo ? resumo.titulo : `Abertos de ${setorDoCaso}`}
          {fila ? ` · ${String(n)} ${n === 1 ? 'caso' : 'casos'}` : ''}
        </b>
      </a>
      {resumo && (
        <div className="cz-fila-contexto">
          <span className="cz-mono">📅 {resumo.periodo}</span>
          {resumo.chips.map((c) => (
            <span key={c} className="cz-fila-chip">
              {c}
            </span>
          ))}
        </div>
      )}
      <div className="cz-fila-lista">
        {!fila ? (
          <p className="cz-fila-vazia">Carregando a fila…</p>
        ) : n === 0 ? (
          <p className="cz-fila-vazia">Nenhum caso nesta fila.</p>
        ) : (
          lista.map((c) => {
            const eh = c.id === atual;
            const tag =
              c.estado === 'AGUARDANDO_DADOS'
                ? 'DADOS'
                : c.estado === 'RECONTAGEM'
                  ? 'RECONTAGEM'
                  : c.estado === 'REABERTA'
                    ? 'REABERTA'
                    : c.estado === 'CONCLUIDA'
                      ? 'CONCLUÍDA'
                      : null;
            return (
              <a
                key={c.id}
                href={linkDe(c.id)}
                className={`cz-fila-item${eh ? ' cz-fila-atual' : ''}${tag === 'DADOS' || tag === 'CONCLUÍDA' ? ' cz-fila-espera' : ''}`}
                aria-current={eh ? 'page' : undefined}
              >
                <span className="cz-fila-linha">
                  <span className="cz-mono">{numeroDoCaso(c.numero)}</span>
                  {eh ? (
                    <span className="cz-tag cz-tag-vermelha">ABERTO</span>
                  ) : tag ? (
                    <span className="cz-tag cz-tag-ambar">{tag}</span>
                  ) : (
                    <span className="cz-fila-idade">{ha(c.abertoEm)}</span>
                  )}
                </span>
                <b>{c.produto}</b>
                <span className="cz-fila-sub">
                  {sinal(c.diferenca)} · {c.setor} B{c.bloco} · {dataCurta(c.abertoEm)}
                  {c.analisadaPor && !eh ? ` · com ${primeiro(c.analisadaPor)}` : ''}
                </span>
              </a>
            );
          })
        )}
      </div>
      <div className="cz-fila-rodape cz-fila-nav">
        <span className="cz-mono">
          {posicao >= 0 ? `${String(posicao + 1)} de ${String(n)}` : `${String(n)} na fila`}
        </span>
        {anterior ? (
          <a href={linkDe(anterior.id)}>← Anterior</a>
        ) : (
          <span className="cz-fila-nav-off">← Anterior</span>
        )}
        {proximo ? (
          <a href={linkDe(proximo.id)}>Próximo →</a>
        ) : (
          <span className="cz-fila-nav-off">Próximo →</span>
        )}
      </div>
    </aside>
  );
}

/** Antes de recontar: já foi reconferido hoje? É reincidente? Ninguém trabalha às cegas. */
function AvisosDoProduto({ caso }: { caso: CasoCompleto }) {
  const hojeDia = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const deHoje = caso.recontagensFeitas.filter(
    (r) =>
      new Date(r.em).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) === hojeDia,
  );
  const ultima = deHoje[deHoje.length - 1];
  const d = caso.diferenca;
  return (
    <div className="cz-avisos-produto">
      <div className={`cz-leitura ${d < 0 ? 'cz-leitura-falta' : d > 0 ? 'cz-leitura-sobra' : ''}`}>
        <span>
          O sistema esperava <b>{caso.solicitado}</b>{' '}
          {caso.solicitado === 1 ? 'unidade' : 'unidades'}. Foram encontradas{' '}
          <b>{caso.encontrado}</b>.
        </span>
        <strong>
          {d < 0 ? `🚨 FALTAM ${String(-d)}` : d > 0 ? `SOBRAM ${String(d)}` : 'SEM DIFERENÇA'}
        </strong>
      </div>
      {ultima && (
        <div className="cz-aviso-ok">
          ✅ Este produto já foi reconferido hoje às {hora(ultima.em)}
          {ultima.por ? ` por ${ultima.por}` : ''} (encontrado {ultima.encontrado}).
        </div>
      )}
      {caso.reincidencias >= 2 && (
        <div className="cz-aviso-reinc">
          ⚠️ {caso.reincidencias}ª divergência deste produto no período de reincidência.
        </div>
      )}
    </div>
  );
}

/* ---------- Cabeçalho (desktop) ---------- */

function Cabecalho({ caso }: { caso: CasoCompleto }) {
  const r = caso.resultado;
  return (
    <header className="cz-cab">
      <div className="cz-cab-esq">
        <div className="cz-cab-linha">
          <span className="cz-mono cz-cab-numero">DIVERGÊNCIA {numeroDoCaso(caso.numero)}</span>
          <span
            className={`cz-selo ${caso.estado === 'CONCLUIDA' ? 'cz-selo-ok' : 'cz-selo-escuro'}`}
          >
            STATUS · {ROTULO_DO_ESTADO[caso.estado].toUpperCase()}
          </span>
          <span className={`cz-selo ${r ? `cz-selo-res-${TOM_DO_RESULTADO[r]}` : 'cz-selo-claro'}`}>
            RESULTADO · {r ? ROTULO_DO_RESULTADO[r].toUpperCase() : '—'}
          </span>
        </div>
        <div className="cz-cab-produto">
          <FotoDoProduto url={caso.fotoProduto} nome={caso.produto} tamanho={76} />
          <div>
            <h1>{caso.produto}</h1>
            <div className="cz-fabricante">{caso.fabricante ?? 'Laboratório não informado'}</div>
          </div>
        </div>
        <div className="cz-cab-meta">
          <span className="cz-mono">
            {[caso.codigo, caso.ean].filter(Boolean).join(' · ') || 'sem código'}
          </span>
          <span className="cz-mono cz-escuro">{faixaDo(caso)}</span>
          <span>{diaBr(caso.dia)}</span>
        </div>
      </div>
      <div className="cz-cab-dir">
        <div className="cz-autoria">
          <Icone nome="user" tamanho={15} traco={1.6} />
          <div>
            <em>AUTORIA DESTE ITEM</em>
            <b>{caso.autoria ?? 'não identificada'}</b>
          </div>
        </div>
        {caso.etiquetas.length > 0 && (
          <div className="cz-etiquetas">
            {caso.etiquetas.map((e) => (
              <span
                key={e}
                className={`cz-tag-m ${e === 'ATRASADA' || e === 'REINCIDENTE' ? 'cz-tag-m-erro' : ''}`}
              >
                {ROTULO_DA_ETIQUETA[e]}
              </span>
            ))}
          </div>
        )}
        <span className="cz-nota">
          {caso.reincidencias} {caso.reincidencias === 1 ? 'caso' : 'casos'} deste produto ·{' '}
          {caso.etiquetas.length} {caso.etiquetas.length === 1 ? 'etiqueta' : 'etiquetas'}
          {caso.analisadaPor && caso.estado !== 'CONCLUIDA'
            ? ` · em análise por ${caso.analisadaPor}`
            : ''}
        </span>
      </div>
    </header>
  );
}

/* ---------- O que aconteceu ---------- */

function Grandezas({
  caso,
  compacta,
  soRodape,
}: {
  caso: CasoCompleto;
  compacta?: boolean;
  soRodape?: boolean;
}) {
  const g = caso.grandezas;
  const estoque = g.estoqueUsado === null ? 'indisponível' : String(g.estoqueUsado);
  const rodape = (
    <div className="cz-grandezas-rodape">
      <span>
        <Icone nome="db" tamanho={13} traco={1.6} />
        Estoque: <b>{g.estoqueUsadoFonte}</b>
        {g.estoqueUsadoEm ? ` · ${hora(g.estoqueUsadoEm)}` : ''}
      </span>
      <span>
        <Icone nome="shelf" tamanho={13} traco={1.6} />
        Contagem: <b>InovaPay</b>
        {g.contadoEm ? ` · ${hora(g.contadoEm)}` : ' · sem hora registrada'}
        {g.entrada ? ` · ${g.entrada === 'SCAN' ? 'escaneado' : 'digitado'}` : ''}
      </span>
      <span>
        <Icone nome="box" tamanho={13} traco={1.6} />
        Reposto:{' '}
        {g.reposto === null ? (
          <b>sem registro</b>
        ) : (
          <>
            <b>{g.reposto}</b> de {g.solicitado} solicitados
          </>
        )}
      </span>
      {g.repostoEm && <span className="cz-chip">reposição às {hora(g.repostoEm)}</span>}
    </div>
  );
  if (soRodape) return rodape;
  if (compacta)
    return (
      <div className="cz-cel-grandezas">
        <div className="cz-tres">
          <div>
            <em>SOLICIT.</em>
            <b>{g.solicitado}</b>
          </div>
          <div>
            <em>ENCONTR.</em>
            <b>{g.encontrado}</b>
          </div>
          <div className="cz-diferenca">
            <em>DIFER.</em>
            <b>{sinal(g.diferenca)}</b>
          </div>
        </div>
        <div className="cz-cel-grandezas-pe">
          Prateleira {g.prateleira ?? 'não localizado'} · alvo {g.alvo} · estoque usado {estoque}
          <small>
            Estoque: {g.estoqueUsadoFonte}
            {g.estoqueUsadoEm ? ` · ${hora(g.estoqueUsadoEm)}` : ''}
          </small>
        </div>
      </div>
    );
  const celula = (rotulo: string, valor: ReactNode, cls = '') => (
    <div className={`cz-grandeza ${cls}`}>
      <em>{rotulo}</em>
      <b>{valor}</b>
    </div>
  );
  return (
    <Cartao
      forte
      titulo="O que aconteceu"
      extra={<span className="cz-nota">valores do momento do registro · não recalculados</span>}
    >
      <div className="cz-seis">
        {celula(
          'PRATELEIRA',
          g.prateleira ?? 'não localizado',
          g.prateleira === null ? 'cz-indisp' : '',
        )}
        {celula('ALVO', g.alvo)}
        {celula(
          'ESTOQUE USADO',
          estoque,
          `cz-grandeza-estoque${g.estoqueUsado === null ? ' cz-indisp' : ''}`,
        )}
        {celula('SOLICITADO', g.solicitado)}
        {celula('ENCONTRADO', g.encontrado)}
        {celula('DIFERENÇA', sinal(g.diferenca), 'cz-diferenca')}
      </div>
      {rodape}
    </Cartao>
  );
}

/* ---------- Linha do tempo ---------- */

const ROTULO_DA_ORIGEM: Readonly<Record<EventoDoCaso['origem'], string>> = {
  humana: 'ação humana',
  sistema: 'sistema',
  externa: 'dado externo',
};

function LinhaDoTempo({ caso, dobravel }: { caso: CasoCompleto; dobravel?: boolean }) {
  const eventos = caso.linhaDoTempo;
  const lista =
    eventos.length === 0 ? (
      <p className="cz-vazio">Nenhum evento registrado para este item.</p>
    ) : (
      <ol className="cz-tempo">
        {eventos.map((e, i) => (
          <li key={`${e.em}-${String(i)}`} className={`cz-tempo-${e.origem}`}>
            <span className="cz-ponto" aria-label={ROTULO_DA_ORIGEM[e.origem]} role="img" />
            <div>
              <div className="cz-tempo-texto">
                <b className="cz-mono">{hora(e.em)}</b> · {e.texto}
              </div>
              {e.detalhe && <div className="cz-tempo-detalhe">{e.detalhe}</div>}
            </div>
          </li>
        ))}
      </ol>
    );
  const legenda = (
    <span className="cz-legenda">
      {(['humana', 'sistema', 'externa'] as const).map((o) => (
        <span key={o} className={`cz-tempo-${o}`}>
          <i />
          {ROTULO_DA_ORIGEM[o]}
        </span>
      ))}
    </span>
  );
  const pe =
    'A tela mostra os eventos na ordem em que aconteceram. Ela não afirma qual deles causou a diferença.';
  if (dobravel)
    return (
      <Dobra icone="clock" titulo="Linha do tempo" aberta>
        {legenda}
        {lista}
        <p className="cz-pe">{pe}</p>
      </Dobra>
    );
  return (
    <Cartao titulo="Linha do tempo" extra={legenda} rodape={pe} className="cz-cartao-tempo">
      <div className="cz-cartao-corpo">{lista}</div>
    </Cartao>
  );
}

/* ---------- Movimentos entre os registros ---------- */

function Movimentos({ caso, solto }: { caso: CasoCompleto; solto?: boolean }) {
  const m = caso.movimentos;
  const g = caso.grandezas;
  type Linha = { em: string; texto: string; qtd: number | null; tom: '' | 'menos' | 'mais' };
  const linhas: Linha[] = [
    ...m.vendas.map((v) => ({
      em: v.em,
      texto: `venda${v.cupom ? ` · cupom ${v.cupom}` : ''}`,
      qtd: -v.qtd,
      tom: 'menos' as const,
    })),
    ...m.reposicoes.map((r) => ({
      em: r.em,
      texto: `reposição em outra missão${r.por ? ` · ${r.por}` : ''}`,
      qtd: r.qtd,
      tom: 'mais' as const,
    })),
  ].sort((a, b) => a.em.localeCompare(b.em));
  const encontradoEm = g.repostoEm ?? g.contadoEm;
  const ultimaRecontagem = caso.recontagensFeitas.at(-1) ?? null;
  const cobertura = m.coberturaCompleta ? (
    <p className="cz-cobertura cz-cobertura-ok">
      <Icone nome="check" tamanho={13} traco={2} />
      Vendas do HOS cobertas até {hora(m.ate)} de {dataCurta(m.ate)}
      {ultimaRecontagem ? ' (a recontagem)' : ''}.
    </p>
  ) : (
    <p className="cz-cobertura cz-cobertura-parcial">
      <Icone nome="warn" tamanho={13} traco={1.7} />
      {m.vendasCobertasAte
        ? `As vendas do HOS só chegaram até ${hora(m.vendasCobertasAte)} de ${dataCurta(m.vendasCobertasAte)}. Vendas depois disso ainda não aparecem aqui.`
        : 'Nenhuma carga de vendas do HOS declarou este período. As vendas ainda não aparecem aqui.'}
    </p>
  );
  const corpo = (
    <div className="cz-mov">
      <div className="cz-mov-linha">
        <span>
          <b className="cz-mono">{g.estoqueUsadoEm ? hora(g.estoqueUsadoEm) : '—'}</b> estoque usado
          no cálculo
        </span>
        <b className={g.estoqueUsado === null ? 'cz-indisp' : ''}>
          {g.estoqueUsado ?? 'indisponível'}
        </b>
      </div>
      {linhas.length === 0 ? (
        <div className="cz-mov-linha cz-mov-nada">
          <span>
            Nenhuma venda ou reposição de outra missão registrada entre {hora(m.desde)} e{' '}
            {hora(m.ate)}
            {m.coberturaCompleta ? '' : ' (até onde as vendas chegaram)'}
          </span>
        </div>
      ) : (
        linhas.map((l, i) => (
          <div className="cz-mov-linha" key={`${l.em}-${String(i)}`}>
            <span>
              <b className="cz-mono">{hora(l.em)}</b> {l.texto}
            </span>
            <b className={`cz-mov-${l.tom}`}>{l.qtd === null ? '—' : sinal(l.qtd)}</b>
          </div>
        ))
      )}
      <div className="cz-mov-linha cz-mov-total">
        <span>Encontrado{encontradoEm ? ` às ${hora(encontradoEm)}` : ''}</span>
        <b>{g.encontrado}</b>
      </div>
      {ultimaRecontagem && (
        <div className="cz-mov-linha cz-mov-recontagem">
          <span>
            <b className="cz-mono">{hora(ultimaRecontagem.em)}</b> recontagem ·{' '}
            {ROTULO_DO_LOCAL[ultimaRecontagem.local].toLowerCase()}
          </span>
          <b>{ultimaRecontagem.encontrado}</b>
        </div>
      )}
      {cobertura}
      <p className="cz-pe cz-pe-solto">
        Eventos vindos da fonte. A reconciliação do saldo é cálculo do servidor — a tela não soma
        nem conclui por conta própria.
      </p>
    </div>
  );
  if (solto) return corpo;
  return (
    <Cartao
      titulo="Movimentos entre os registros"
      extra={<span className="cz-fonte">CALCULADO PELO SERVIDOR</span>}
    >
      <div className="cz-cartao-corpo">{corpo}</div>
    </Cartao>
  );
}

/* ---------- Evidências ---------- */

function Foto({ id }: { id: string }) {
  const [erro, setErro] = useState(false);
  if (erro)
    return (
      <div className="cz-foto cz-foto-erro" role="img" aria-label="Foto indisponível">
        <Icone nome="warn" tamanho={18} traco={1.6} />
        <span>Não foi possível carregar</span>
      </div>
    );
  return (
    <a className="cz-foto" href={`/api/evidencias/${id}`} target="_blank" rel="noreferrer">
      <img
        src={`/api/evidencias/${id}`}
        alt="Evidência"
        loading="lazy"
        onError={() => setErro(true)}
      />
    </a>
  );
}

function Evidencias({ caso, solto }: { caso: CasoCompleto; solto?: boolean }) {
  const corpo =
    caso.fotos.length === 0 ? (
      <p className="cz-vazio">
        Nenhuma foto neste caso. A foto da contagem só existe quando o item foi sorteado para
        auditoria rápida.
      </p>
    ) : (
      <div className="cz-fotos">
        {caso.fotos.map((f) => (
          <figure key={f.id}>
            <Foto id={f.id} />
            <figcaption>
              <b>{f.tipo === 'contagem' ? 'Foto da prateleira' : 'Foto da recontagem'}</b>
              <span>
                {primeiro(f.por) || 'Autor não identificado'} · {hora(f.em)} · {f.tipo}
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    );
  const pe =
    'Fotos originais são evidência: o auditor não edita nem substitui. As da recontagem entram como tipo separado.';
  if (solto)
    return (
      <>
        {corpo}
        <p className="cz-pe">{pe}</p>
      </>
    );
  return (
    <Cartao
      titulo="Evidências"
      extra={<span className="cz-nota">{contagemDeFotos(caso)}</span>}
      rodape={pe}
    >
      <div className="cz-cartao-corpo">{corpo}</div>
    </Cartao>
  );
}

/* ---------- As duas contagens convivem ---------- */

function DuasContagens({ caso }: { caso: CasoCompleto }) {
  const g = caso.grandezas;
  const fotoOriginal = caso.fotos.some((f) => f.tipo === 'contagem');
  const quandoOriginal = g.repostoEm ?? g.contadoEm;
  return (
    <Cartao titulo="As duas contagens convivem">
      <div className="cz-cartao-corpo">
        <div className="cz-contagens">
          <div className="cz-contagem">
            <span className="cz-avatar">{iniciais(caso.autoria)}</span>
            <div>
              <b>Registro original · {caso.autoria ?? 'não identificada'}</b>
              <span>
                {quandoOriginal ? quando(quandoOriginal) : 'sem hora'}
                {g.entrada ? ` · ${g.entrada === 'SCAN' ? 'escaneado' : 'digitado'}` : ''}
                {fotoOriginal ? ' · com foto' : ''} · encontrados
              </span>
            </div>
            <strong>{g.encontrado}</strong>
          </div>
          {caso.recontagensFeitas.map((r) => (
            <div key={r.id} className="cz-contagem cz-contagem-nova">
              <span className="cz-avatar">{iniciais(r.por)}</span>
              <div>
                <b>Recontagem · {r.por ?? 'auditor'}</b>
                <span>
                  {quando(r.em)} · {ROTULO_DO_LOCAL[r.local].toLowerCase()}
                  {r.fotoId ? ' · com foto' : ''}
                </span>
                {r.observacao && <span className="cz-contagem-obs">“{r.observacao}”</span>}
              </div>
              <strong>{r.encontrado}</strong>
            </div>
          ))}
        </div>
        <p className="cz-pe cz-pe-solto">
          A auditoria acrescenta evidência; não reescreve o passado. O valor registrado pela
          balconista não é alterado.
        </p>
        {caso.sugestao && caso.estado !== 'CONCLUIDA' && (
          <p className="cz-sugestao">
            Sugestão do sistema: <b>{ROTULO_DO_RESULTADO[caso.sugestao.sugestao]}</b> —{' '}
            {caso.sugestao.porque}. A decisão é do auditor.
          </p>
        )}
      </div>
    </Cartao>
  );
}

/* ---------- Conclusão (leitura) ---------- */

function ultimaConclusao(caso: CasoCompleto): AuditoriaDoCaso | null {
  return [...caso.auditorias].reverse().find((a) => a.reabertura === null) ?? null;
}

function Conclusao({
  caso,
  anterior,
  seguir,
}: {
  caso: CasoCompleto;
  anterior?: boolean;
  seguir?:
    | {
        proximo: CasoDaFila | null;
        restantes: number;
        linkDe: (id: string) => string;
        voltarPara: string;
      }
    | undefined;
}) {
  const a = ultimaConclusao(caso);
  const reab = [...caso.auditorias].reverse().find((x) => x.reabertura !== null) ?? null;
  if (!a) return null;
  const tom = TOM_DO_RESULTADO[a.resultado];
  return (
    <div
      className={`cz-cartao cz-conclusao cz-conclusao-${tom}${anterior ? ' cz-conclusao-anterior' : ''}`}
    >
      <div className="cz-conclusao-topo">
        {!anterior && (
          <span className="cz-conclusao-icone">
            <Icone nome="check" tamanho={18} traco={2.2} />
          </span>
        )}
        <b>{anterior ? 'Conclusão anterior (antes da reabertura)' : 'Auditoria concluída'}</b>
        <span className="cz-mono">caso {numeroDoCaso(caso.numero)}</span>
      </div>
      <dl className="cz-dl">
        <div>
          <dt>Status</dt>
          <dd>{anterior ? 'Reaberta' : 'Concluída'}</dd>
        </div>
        <div>
          <dt>Resultado</dt>
          <dd className={`cz-res-${tom}`}>{ROTULO_DO_RESULTADO[a.resultado]}</dd>
        </div>
        <div>
          <dt>Por</dt>
          <dd>
            {a.por ?? 'auditor não identificado'} · {quando(a.em)}
          </dd>
        </div>
        {a.encontrado !== null && (
          <div>
            <dt>Recontagem usada</dt>
            <dd>{a.encontrado}</dd>
          </div>
        )}
      </dl>
      {a.observacao && (
        <div className="cz-motivo">
          <em>MOTIVO</em>
          <p>{a.observacao}</p>
        </div>
      )}
      {a.escolhido && a.escolhido !== a.resultado && (
        <div className="cz-rebaixado">
          <Icone nome="warn" tamanho={15} traco={1.7} />
          <p>
            O auditor escolheu <b>{TITULO_DA_OPCAO[a.escolhido]}</b>; o servidor registrou{' '}
            <b>{ROTULO_DO_RESULTADO[a.resultado]}</b>
            {a.rebaixado ? ` porque ${a.rebaixado}.` : '.'}
          </p>
        </div>
      )}
      {anterior && reab && (
        <div className="cz-motivo">
          <em>
            REABERTA POR {(reab.por ?? 'administrador').toUpperCase()} · {quando(reab.em)}
          </em>
          <p>{reab.reabertura}</p>
        </div>
      )}
      {seguir && (
        <div className="cz-pad cz-seguir">
          <BotaoDoProximo {...seguir} />
        </div>
      )}
      <p className="cz-pe cz-pe-solto">
        Status e resultado continuam separados também no fechamento.
        {!anterior && ' Caso concluído é leitura.'}
      </p>
    </div>
  );
}

/* ---------- Depois de concluir agora ---------- */

function BotaoDoProximo({
  proximo,
  restantes,
  linkDe,
  voltarPara,
}: {
  proximo: CasoDaFila | null;
  restantes: number;
  linkDe: (id: string) => string;
  voltarPara: string;
}) {
  return proximo ? (
    <a className="cz-botao cz-botao-primario cz-botao-g" href={linkDe(proximo.id)}>
      Ir para o próximo → · {restantes} na fila
    </a>
  ) : (
    <a className="cz-botao cz-botao-contorno cz-botao-g" href={voltarPara}>
      Fila terminada · voltar para a lista
    </a>
  );
}

function Fechamento({
  r,
  proximo,
  restantes,
  concluido,
  linkDe,
  voltarPara,
}: {
  concluido: boolean;
  r: ResultadoDaConclusao;
  proximo: CasoDaFila | null;
  restantes: number;
  linkDe: (id: string) => string;
  voltarPara: string;
}) {
  const rebaixou = r.escolhido !== r.resultado;
  const aqui = useRef<HTMLDivElement>(null);
  useEffect(() => {
    aqui.current?.scrollIntoView({ block: 'center' });
  }, []);
  return (
    <div className="cz-cartao cz-fechamento" role="status" ref={aqui}>
      {rebaixou ? (
        <div className="cz-rebaixado cz-rebaixado-forte">
          <Icone nome="warn" tamanho={17} traco={1.7} />
          <div>
            <b>Registrado como {ROTULO_DO_RESULTADO[r.resultado]}</b>
            <p>
              Você escolheu {TITULO_DA_OPCAO[r.escolhido]}, mas houve movimento no período:{' '}
              {r.movimentos.map((m) => m.texto).join('; ') || r.rebaixado}. Com movimento que pode
              explicar a diferença, o servidor não confirma erro de contagem.
            </p>
          </div>
        </div>
      ) : (
        <div className="cz-fechamento-ok">
          <Icone nome="check" tamanho={17} traco={2.2} />
          <b>Conclusão gravada: {ROTULO_DO_RESULTADO[r.resultado]}</b>
        </div>
      )}
      {r.bloqueioAplicado && (
        <p className="cz-pe cz-pe-solto">
          A regra de bloqueio configurada foi reavaliada pelo servidor para esta pessoa.
        </p>
      )}
      {!concluido && (
        <BotaoDoProximo
          proximo={proximo}
          restantes={restantes}
          linkDe={linkDe}
          voltarPara={voltarPara}
        />
      )}
    </div>
  );
}

/* ---------- Histórico: a auditoria da auditoria ---------- */

function Historico({ caso }: { caso: CasoCompleto }) {
  type Item = { em: string; quem: string | null; texto: string; detalhe: string | null };
  const itens: Item[] = [
    { em: caso.abertoEm, quem: null, texto: 'Caso criado pelo sistema', detalhe: null },
    ...caso.recontagensFeitas.map((r) => ({
      em: r.em,
      quem: r.por,
      texto: `registrou recontagem de ${String(r.encontrado)} (${ROTULO_DO_LOCAL[r.local].toLowerCase()})`,
      detalhe: r.observacao,
    })),
    ...caso.notas.map((n) => ({
      em: n.em,
      quem: n.por,
      texto:
        n.tipo === 'ENCAMINHAMENTO'
          ? `encaminhou${n.destino ? ` · ${DESTINOS.find((d) => d.id === n.destino)?.rotulo.toLowerCase() ?? ''}` : ''}`
          : n.tipo === 'RETOMADA'
            ? 'retomou a análise'
            : 'registrou observação',
      detalhe: n.texto,
    })),
    ...caso.auditorias.map((a) => ({
      em: a.em,
      quem: a.por,
      texto: a.reabertura
        ? 'reabriu · motivo registrado'
        : `concluiu · ${ROTULO_DO_RESULTADO[a.resultado].toLowerCase()}${a.escolhido && a.escolhido !== a.resultado ? ` (escolhido: ${TITULO_DA_OPCAO[a.escolhido].toLowerCase()})` : ''}`,
      detalhe: a.reabertura ?? a.observacao,
    })),
  ].sort((a, b) => a.em.localeCompare(b.em));
  return (
    <Cartao
      titulo="Histórico da auditoria"
      rodape="Quem abriu, analisou, concluiu, encaminhou e reabriu — com autor, hora e motivo. Observação é anexo: nunca edita o evento original."
    >
      <ol className="cz-hist">
        {itens.map((i, k) => (
          <li key={`${i.em}-${String(k)}`}>
            <span className="cz-mono">
              {dataCurta(i.em)} {hora(i.em)}
            </span>
            <div>
              <p>
                {i.quem ? <b>{primeiro(i.quem)} </b> : null}
                {i.texto}
              </p>
              {i.detalhe && <small>{i.detalhe}</small>}
            </div>
          </li>
        ))}
      </ol>
    </Cartao>
  );
}

/* ---------- Avisos das escritas ---------- */

function AvisoDaEscrita(p: PropsDoCaso & { aviso: Aviso }) {
  const aqui = useRef<HTMLDivElement>(null);
  // O aviso aparece onde a pessoa está olhando: a ação pode ter sido feita lá embaixo.
  useEffect(() => {
    aqui.current?.scrollIntoView({ block: 'nearest' });
  }, [p.aviso]);
  return (
    <div ref={aqui} className="cz-aviso-lugar">
      <ConteudoDoAviso {...p} />
    </div>
  );
}

function ConteudoDoAviso({ aviso, setAviso, carregar }: PropsDoCaso & { aviso: Aviso }) {
  if (aviso.tipo === 'conflito')
    return (
      <div className="cz-aviso cz-aviso-info" role="alert">
        <b>Este caso foi atualizado por outro usuário</b>
        <p>
          Alguém mexeu neste caso enquanto você analisava. A tela já mostra a versão atual: confira
          antes de repetir a ação.
        </p>
        <button
          type="button"
          className="cz-botao cz-botao-info"
          onClick={() => {
            setAviso(null);
            void carregar();
          }}
        >
          Recarregar
        </button>
      </div>
    );
  if (aviso.tipo === 'incerta')
    return (
      <div className="cz-aviso cz-aviso-escuro" role="alert">
        <Icone nome="offline" tamanho={17} traco={1.6} />
        <div>
          <b>{aviso.titulo}: sem confirmação do servidor</b>
          <p>
            A conexão caiu antes da resposta. Não sabemos se gravou — recarregue o caso antes de
            tentar de novo.
          </p>
          <button
            type="button"
            className="cz-botao cz-botao-primario"
            onClick={() => {
              setAviso(null);
              void carregar();
            }}
          >
            <Icone nome="retry" tamanho={15} traco={1.7} /> Recarregar o caso
          </button>
        </div>
      </div>
    );
  if (aviso.tipo === 'recusada')
    return (
      <div className="cz-aviso cz-aviso-erro" role="alert">
        <Icone nome="warn" tamanho={16} traco={1.7} />
        <div>
          <b>{aviso.titulo}</b>
          <p>{aviso.texto}</p>
        </div>
      </div>
    );
  return (
    <div className="cz-aviso cz-aviso-ok" role="status">
      <Icone nome="check" tamanho={16} traco={2} />
      <div>
        <b>{aviso.titulo}</b>
        {aviso.texto && <p>{aviso.texto}</p>}
      </div>
    </div>
  );
}

/* ================================================================== *
 * Ações
 * ================================================================== */

function Formulario(
  p: PropsDoCaso & {
    modo: Modo;
    aoConcluir: (r: ResultadoDaConclusao) => void;
  },
) {
  const { modo, aviso } = p;
  const avisoAqui = aviso && modo !== 'ver' ? <AvisoDaEscrita {...p} aviso={aviso} /> : null;
  if (modo === 'recontar')
    return (
      <>
        {avisoAqui}
        <Recontagem {...p} />
      </>
    );
  if (modo === 'concluir')
    return (
      <>
        {avisoAqui}
        <Concluir {...p} />
      </>
    );
  if (modo === 'observar' || modo === 'encaminhar')
    return (
      <>
        {avisoAqui}
        <Anotar {...p} encaminhar={modo === 'encaminhar'} />
      </>
    );
  return <ProximoPasso {...p} />;
}

function ProximoPasso({ caso, trocarModo }: PropsDoCaso) {
  return (
    <Cartao titulo="Próximo passo" forte>
      <div className="cz-passos">
        <button
          type="button"
          className="cz-botao cz-botao-contorno cz-botao-m"
          onClick={() => trocarModo('recontar')}
        >
          <Icone nome="scan" tamanho={17} traco={1.7} /> Fazer recontagem
        </button>
        <button
          type="button"
          className="cz-botao cz-botao-leve cz-botao-m"
          onClick={() => trocarModo('observar')}
        >
          Registrar observação
        </button>
        <button
          type="button"
          className="cz-botao cz-botao-leve cz-botao-m"
          onClick={() => trocarModo('encaminhar')}
        >
          Encaminhar caso
        </button>
        <div className="cz-passos-fim">
          <p>
            {caso.recontagensFeitas.length === 0
              ? 'O resultado da auditoria fica disponível depois de revisar as evidências.'
              : 'Evidências revisadas? O resultado fica registrado com o seu motivo.'}
          </p>
          <button
            type="button"
            className="cz-botao cz-botao-primario cz-botao-g"
            onClick={() => trocarModo('concluir')}
          >
            Concluir auditoria
          </button>
        </div>
      </div>
    </Cartao>
  );
}

/* ---------- Recontagem ---------- */

function Recontagem({ caso, sessao, eu, tratar, carregar, trocarModo, setAviso }: PropsDoCaso) {
  const [qtd, setQtd] = useState<number | null>(null);
  const [local, setLocal] = useState<LocalDaRecontagem>('PRATELEIRA');
  const [obs, setObs] = useState('');
  const [foto, setFoto] = useState<{ id: string; previa: string } | null>(null);
  const [fotoEstado, setFotoEstado] = useState<string>('parado');
  const [camera, setCamera] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const exigeFoto = caso.config.recontagemExigeFoto;
  const g = caso.grandezas;
  const autora = primeiro(caso.autoria) || 'da balconista';

  // A prévia é um arquivo na memória: solta quando troca ou quando a tela sai.
  useEffect(
    () => () => {
      if (foto) URL.revokeObjectURL(foto.previa);
    },
    [foto],
  );

  const tirar = (arq: FonteDaFoto | undefined) => {
    if (!arq) return;
    setFotoEstado('enviando');
    void (async () => {
      try {
        const blob = await carimbarFoto(arq, [
          `Recontagem ${numeroDoCaso(caso.numero)} · ${caso.produto}`,
          `${eu?.nomeExibicao ?? ''} · ${agoraCarimbo()}`,
        ]);
        const up = await enviarFoto(sessao, blob);
        if (!up.ok) {
          setFotoEstado(up.mensagem);
          return;
        }
        setFoto({ id: up.id, previa: URL.createObjectURL(blob) });
        setFotoEstado('parado');
      } catch {
        setFotoEstado('Não foi possível preparar a foto. Tente de novo.');
      }
    })();
  };

  const pronto = qtd !== null && (!exigeFoto || foto !== null) && !enviando;
  const registrar = async () => {
    if (qtd === null) return;
    setEnviando(true);
    setAviso(null);
    const r = await registrarRecontagem(sessao, caso.id, {
      encontrado: qtd,
      local,
      observacao: obs.trim() || null,
      evidenciaId: foto?.id ?? null,
    });
    setEnviando(false);
    const d = tratar(r, 'A recontagem');
    if (!d) return;
    trocarModo('ver');
    setAviso({
      tipo: 'ok',
      titulo: `Recontagem registrada: ${unidades(qtd)}`,
      texto: `Sugestão do sistema: ${ROTULO_DO_RESULTADO[d.sugestao.sugestao]} — ${d.sugestao.porque}. A decisão continua sendo sua.`,
    });
    await carregar();
  };

  return (
    <div className="cz-form">
      <div className="cz-cartao cz-original">
        <div>
          <em>REGISTRO ORIGINAL</em>
          <b>
            {caso.autoria ?? 'Balconista'} · <span>{g.encontrado}</span> encontrados
          </b>
          <small>
            {(g.repostoEm ?? g.contadoEm)
              ? `${hora((g.repostoEm ?? g.contadoEm) as string)} · `
              : ''}
            permanece registrado
          </small>
        </div>
      </div>
      <div className="cz-cartao cz-stepper-cartao">
        <h3>
          Quantas unidades você
          <br />
          encontrou agora?
        </h3>
        <div className="cz-stepper">
          <button
            type="button"
            aria-label="Menos uma"
            onClick={() => setQtd((q) => Math.max(0, (q ?? 1) - 1))}
          >
            <Icone nome="minus" tamanho={20} traco={1.8} />
          </button>
          <input
            inputMode="numeric"
            aria-label="Unidades encontradas na recontagem"
            placeholder="—"
            value={qtd === null ? '' : String(qtd)}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 4);
              setQtd(v === '' ? null : Number(v));
            }}
          />
          <button
            type="button"
            aria-label="Mais uma"
            onClick={() => setQtd((q) => Math.min(9999, (q ?? -1) + 1))}
          >
            <Icone nome="plus" tamanho={20} traco={1.8} />
          </button>
        </div>
        <p>
          Sua recontagem entra como evidência nova. O valor{' '}
          {autora === 'da balconista' ? autora : `da ${autora}`} não é alterado.
        </p>
      </div>
      <div className="cz-cartao cz-bloco">
        <h3>Onde você encontrou?</h3>
        <div className="cz-pilulas" role="radiogroup" aria-label="Onde você encontrou">
          {(['PRATELEIRA', 'OUTRA_EXPOSICAO', 'ESTOQUE'] as const).map((l) => (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={local === l}
              className={local === l ? 'cz-pilula-sel' : ''}
              onClick={() => setLocal(l)}
            >
              {l === 'OUTRA_EXPOSICAO' && <Icone nome="pin" tamanho={13} traco={1.6} />}
              {ROTULO_DO_LOCAL[l]}
            </button>
          ))}
        </div>
      </div>
      <label className="cz-cartao cz-bloco">
        <h3>
          Observação <span>· opcional</span>
        </h3>
        <textarea
          rows={2}
          maxLength={500}
          value={obs}
          placeholder="Ex.: 2 unidades estavam na ponta de gôndola…"
          onChange={(e) => setObs(e.target.value)}
        />
      </label>
      <div className={`cz-cartao cz-foto-rec${exigeFoto && !foto ? ' cz-foto-rec-exigida' : ''}`}>
        {foto ? (
          <img src={foto.previa} alt="Foto da recontagem" />
        ) : (
          <Icone nome="cam" tamanho={16} traco={1.6} />
        )}
        <div>
          {foto
            ? 'Foto da recontagem enviada'
            : exigeFoto
              ? 'Foto da recontagem obrigatória neste caso'
              : 'Foto da recontagem · opcional'}
          {fotoEstado !== 'parado' && fotoEstado !== 'enviando' && (
            <small className="cz-erro-texto">{fotoEstado}</small>
          )}
        </div>
        <button
          type="button"
          className="cz-link-botao"
          disabled={fotoEstado === 'enviando'}
          onClick={() => setCamera(true)}
        >
          {fotoEstado === 'enviando' ? 'Enviando…' : foto ? 'Trocar foto' : 'Abrir câmera'}
        </button>
        {camera && (
          <CameraDaFoto
            titulo="Foto da recontagem"
            aoFechar={() => setCamera(false)}
            aoUsar={(f) => {
              setCamera(false);
              tirar(f);
            }}
          />
        )}
      </div>
      <div className="cz-acoes">
        <button
          type="button"
          className="cz-botao cz-botao-primario cz-botao-g"
          disabled={!pronto}
          onClick={() => void registrar()}
        >
          {enviando ? 'Registrando…' : 'Registrar recontagem'}
        </button>
        <button
          type="button"
          className="cz-botao cz-botao-leve cz-so-largo"
          onClick={() => trocarModo('ver')}
        >
          Voltar
        </button>
      </div>
    </div>
  );
}

/* ---------- Observação e encaminhamento ---------- */

function Anotar({
  caso,
  sessao,
  tratar,
  carregar,
  trocarModo,
  setAviso,
  encaminhar,
}: PropsDoCaso & { encaminhar: boolean }) {
  const [texto, setTexto] = useState('');
  const [destino, setDestino] = useState<Destino | null>(null);
  const [enviando, setEnviando] = useState(false);
  const pronto = texto.trim().length >= 3 && (!encaminhar || destino !== null) && !enviando;
  const enviar = async () => {
    setEnviando(true);
    setAviso(null);
    const r = await anotar(sessao, caso.id, {
      texto: texto.trim(),
      encaminharPara: encaminhar ? destino : null,
      versao: caso.versao,
    });
    setEnviando(false);
    const d = tratar(r, encaminhar ? 'O encaminhamento' : 'A observação');
    if (!d) return;
    trocarModo('ver');
    setAviso({
      tipo: 'ok',
      titulo: encaminhar
        ? `Caso encaminhado · ${ROTULO_DO_ESTADO[d.estado].toLowerCase()}`
        : 'Observação registrada',
    });
    await carregar();
  };
  return (
    <div className="cz-form">
      <div className="cz-cartao cz-bloco cz-cartao-forte">
        <h3>{encaminhar ? 'Encaminhar para' : 'Registrar observação'}</h3>
        {encaminhar && (
          <div className="cz-pilulas" role="radiogroup" aria-label="Encaminhar para">
            {DESTINOS.map((d) => (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={destino === d.id}
                className={destino === d.id ? 'cz-pilula-sel' : ''}
                onClick={() => setDestino(d.id)}
              >
                {d.rotulo}
              </button>
            ))}
          </div>
        )}
        <label className="cz-rotulo">
          {encaminhar ? 'O que precisa ser visto' : 'Observação'}{' '}
          <span>· obrigatório, mínimo 3 caracteres</span>
          <textarea
            rows={3}
            maxLength={1000}
            value={texto}
            placeholder={
              encaminhar
                ? 'Ex.: saldo do HOS parece antigo; conferir a carga das 09:42…'
                : 'Ex.: produto também exposto no balcão de promoções…'
            }
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <p className="cz-pe cz-pe-solto">
          Observação é anexo com autor e hora: não edita nenhum registro anterior.
          {encaminhar &&
            ' O caso sai da fila de análise e espera o destino (dados/administração) ou a recontagem na loja.'}
        </p>
        <div className="cz-acoes cz-acoes-linha">
          <button
            type="button"
            className="cz-botao cz-botao-primario cz-botao-m cz-flex"
            disabled={!pronto}
            onClick={() => void enviar()}
          >
            {enviando ? 'Enviando…' : encaminhar ? 'Encaminhar caso' : 'Registrar observação'}
          </button>
          <button
            type="button"
            className="cz-botao cz-botao-leve cz-botao-m"
            onClick={() => trocarModo('ver')}
          >
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Copiloto ---------- */

/**
 * As MESMAS recusas do servidor (`concluirCaso`), lidas antes do clique: o auditor não escolhe
 * "erro de contagem" para depois ouvir não (dono, 05/10 — o Felipe tentou 9 vezes).
 */
function travaDoErroDeContagem(caso: CasoCompleto, tresEtapas = false): string | null {
  if (caso.deEstoque)
    return 'Caso de estoque (prateleira maior que o sistema) não é erro do balconista.';
  // Três etapas (dono, 13/10): a evidência é a busca do estoque, que o servidor confere — e venda
  // não muda a conclusão, então não se espera a leitura das vendas do HOS.
  if (tresEtapas) return null;
  if (caso.recontagensFeitas.length === 0)
    return 'Faça a recontagem deste produto primeiro: o erro só se confirma com ela.';
  const m = caso.movimentos;
  if (!m.coberturaCompleta)
    return m.vendasCobertasAte
      ? `Espere a próxima leitura do HOS (a cada ~5 min): as vendas só chegaram até ${hora(m.vendasCobertasAte)} de ${dataCurta(m.vendasCobertasAte)}.`
      : 'Espere a próxima leitura do HOS (a cada ~5 min).';
  return null;
}

type EstadoDoPasso = 'feito' | 'agora' | 'depois';
interface Passo {
  readonly estado: EstadoDoPasso;
  readonly titulo: string;
  readonly texto: ReactNode;
}

/**
 * Copiloto do caso (dono, 05/10): olha o produto DESTE caso e diz o próximo passo, com as regras
 * do servidor. Não decide nada: sugere, e o auditor escolhe e escreve o motivo.
 */
function Copiloto({ caso, trocarModo, escolher, carregar, estreito, irAoFormulario }: PropsDoCaso) {
  const [conferindo, setConferindo] = useState(false);
  // A lista vem em ordem de registro; o servidor julga pela ÚLTIMA recontagem.
  const ultima = caso.recontagensFeitas[caso.recontagensFeitas.length - 1] ?? null;
  const m = caso.movimentos;
  const onde = [
    caso.codigo ? `cód. ${caso.codigo}` : null,
    caso.ean ? `EAN ${caso.ean}` : null,
    `${caso.setor} B${String(caso.bloco)}`,
    caso.faixa,
  ]
    .filter(Boolean)
    .join(' · ');

  const conferirDeNovo = async () => {
    setConferindo(true);
    await carregar();
    setConferindo(false);
  };

  const passos: Passo[] = [];
  let acoes: ReactNode = null;

  if (caso.deEstoque) {
    // Prateleira > sistema: é estoque/cadastro, nunca erro do balconista (o servidor recusa).
    passos.push({
      estado: 'agora',
      titulo: 'Confira a prateleira',
      texto: (
        <>
          O balconista contou <b>{String(caso.prateleira ?? '?')}</b> na prateleira e o sistema diz
          ter <b>{String(caso.estoqueAtual ?? '?')}</b>. Veja se o produto está mesmo lá.
        </>
      ),
    });
    passos.push({
      estado: 'depois',
      titulo: 'Conclua',
      texto: (
        <>
          <b>Está lá</b> → Problema de dados (o estoque do HOS precisa de ajuste). <b>Não está</b> →
          Inconclusivo. Este caso nunca fecha como erro de contagem.
        </>
      ),
    });
    acoes = (
      <>
        <button
          type="button"
          className="cz-botao cz-botao-primario cz-botao-m"
          onClick={() => escolher('PROBLEMA_DADOS')}
        >
          Está lá → Problema de dados
        </button>
        <button
          type="button"
          className="cz-botao cz-botao-leve cz-botao-m"
          onClick={() => escolher('INCONCLUSIVO')}
        >
          Não está → Inconclusivo
        </button>
      </>
    );
  } else if (!ultima) {
    // Reposição: o balconista não achou tudo. Primeiro, ir lá procurar este produto.
    passos.push({
      estado: 'agora',
      titulo: 'Procure SÓ este produto',
      texto: (
        <>
          Pediram <b>{String(caso.solicitado)}</b> e o balconista achou{' '}
          <b>{String(caso.encontrado)}</b>. Procure na prateleira e no estoque.
        </>
      ),
    });
    passos.push({
      estado: 'depois',
      titulo: 'Diga o que encontrou',
      texto: (
        <>
          <b>Também não achei</b> → o balconista estava certo, conclua sem recontar.{' '}
          <b>Achei mais</b> → registre a recontagem (é a prova do erro). <b>Mudou de lugar</b> →
          produto mudou de local.
        </>
      ),
    });
    acoes = (
      <>
        <button
          type="button"
          className="cz-botao cz-botao-primario cz-botao-m"
          onClick={() => escolher('BALCONISTA_CORRETO')}
        >
          Também não achei
        </button>
        <button
          type="button"
          className="cz-botao cz-botao-contorno cz-botao-m"
          onClick={() => {
            trocarModo('recontar');
            if (!estreito) irAoFormulario();
          }}
        >
          <Icone nome="scan" tamanho={16} traco={1.7} /> Achei mais → recontar
        </button>
        <button
          type="button"
          className="cz-botao cz-botao-leve cz-botao-m"
          onClick={() => escolher('PRODUTO_MUDOU_LOCAL')}
        >
          Mudou de lugar
        </button>
      </>
    );
  } else {
    // Já recontou: o que a recontagem e as vendas do HOS dizem.
    passos.push({
      estado: 'feito',
      titulo: 'Recontagem registrada',
      texto: (
        <>
          Você achou <b>{String(ultima.encontrado)}</b> às {hora(ultima.em)}; o balconista tinha
          achado <b>{String(caso.encontrado)}</b>.
        </>
      ),
    });
    if (ultima.encontrado <= caso.encontrado) {
      passos.push({
        estado: 'agora',
        titulo: 'Conclua',
        texto: (
          <>
            Você não achou mais do que ele: <b>Balconista correto</b>.
          </>
        ),
      });
      acoes = (
        <button
          type="button"
          className="cz-botao cz-botao-primario cz-botao-m"
          onClick={() => escolher('BALCONISTA_CORRETO')}
        >
          Concluir como Balconista correto
        </button>
      );
    } else if (!m.coberturaCompleta) {
      passos.push({
        estado: 'agora',
        titulo: 'Espere a leitura do HOS',
        texto: (
          <>
            O sistema precisa ver se alguém vendeu este produto entre a contagem e a sua recontagem.
            As vendas chegaram até <b>{m.vendasCobertasAte ? hora(m.vendasCobertasAte) : '—'}</b>; a
            próxima leitura sai em até ~5 min. Pode seguir para o próximo caso e voltar depois.
          </>
        ),
      });
      acoes = (
        <button
          type="button"
          className="cz-botao cz-botao-contorno cz-botao-m"
          disabled={conferindo}
          onClick={() => void conferirDeNovo()}
        >
          {conferindo ? 'Conferindo…' : 'Já chegou? Conferir de novo'}
        </button>
      );
    } else if (m.movimentos.length > 0) {
      passos.push({
        estado: 'agora',
        titulo: 'Conclua',
        texto: (
          <>
            Houve movimento no período ({m.movimentos.map((x) => x.texto).join('; ')}): a diferença
            pode ter vindo daí. <b>Inconclusivo</b>.
          </>
        ),
      });
      acoes = (
        <button
          type="button"
          className="cz-botao cz-botao-primario cz-botao-m"
          onClick={() => escolher('INCONCLUSIVO')}
        >
          Concluir como Inconclusivo
        </button>
      );
    } else {
      passos.push({
        estado: 'feito',
        titulo: 'Vendas do HOS conferidas',
        texto: 'Nada vendido nem reposto no período.',
      });
      passos.push({
        estado: 'agora',
        titulo: 'Conclua',
        texto: (
          <>
            Você achou mais do que o balconista e nada explica a diferença:{' '}
            <b>Erro de contagem confirmado</b>.
          </>
        ),
      });
      acoes = (
        <button
          type="button"
          className="cz-botao cz-botao-primario cz-botao-m"
          onClick={() => escolher('ERRO_BALCONISTA_CONFIRMADO')}
        >
          Concluir como Erro de contagem
        </button>
      );
    }
  }

  return (
    <section className="cz-cartao cz-cartao-forte cz-copiloto" aria-label="Copiloto do caso">
      <div className="cz-copiloto-topo">
        <Icone nome="check" tamanho={15} traco={2} />
        <b>Copiloto · o que fazer agora</b>
      </div>
      <div className="cz-copiloto-produto">
        <b>{caso.produto}</b>
        <span className="cz-mono">{onde}</span>
      </div>
      <ol className="cz-copiloto-passos">
        {passos.map((x, i) => (
          <li key={x.titulo} className={`cz-copiloto-passo cz-copiloto-${x.estado}`}>
            <span className="cz-copiloto-n" aria-hidden="true">
              {x.estado === 'feito' ? '✓' : String(i + 1)}
            </span>
            <div>
              <b>{x.titulo}</b>
              <p>{x.texto}</p>
            </div>
          </li>
        ))}
      </ol>
      {acoes && <div className="cz-copiloto-acoes">{acoes}</div>}
    </section>
  );
}

/* ---------- Concluir ---------- */

function Concluir({
  caso,
  sessao,
  tratar,
  carregar,
  trocarModo,
  setAviso,
  aoConcluir,
  escolhaInicial,
  etapas,
  pular,
}: PropsDoCaso & { aoConcluir: (r: ResultadoDaConclusao) => void }) {
  const [escolha, setEscolha] = useState<ResultadoDaAuditoria | null>(escolhaInicial);
  const [motivo, setMotivo] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [desfecho, setDesfecho] = useState<DesfechoDoCaso | ''>('');
  const tresEtapas = etapas?.ativo === true;
  const recontou = caso.recontagensFeitas.length > 0;
  const m = caso.movimentos;
  const pronto = escolha !== null && motivo.trim().length >= 5 && !enviando;

  const enviar = async () => {
    if (!escolha) return;
    setConfirmando(false);
    setEnviando(true);
    setAviso(null);
    const r = await concluir(sessao, caso.id, {
      resultado: escolha,
      observacao: motivo.trim(),
      versao: caso.versao,
      ...(tresEtapas && desfecho ? { desfecho } : {}),
      ...(tresEtapas && pular ? { pularEtapas: true } : {}),
    });
    setEnviando(false);
    const d = tratar(r, 'A conclusão', tresEtapas);
    if (!d) return;
    aoConcluir(d);
    trocarModo('ver');
    await carregar();
  };

  /** Por que o servidor recusaria "erro de contagem" agora; null = pode escolher. */
  const travaDoErro = travaDoErroDeContagem(caso, tresEtapas);
  const dicaDo = (r: ResultadoDaAuditoria): string | null => {
    if (r !== 'ERRO_BALCONISTA_CONFIRMADO') return null;
    if (travaDoErro) return travaDoErro;
    if (!tresEtapas && m.movimentos.length > 0)
      return `Há movimento no período (${m.movimentos.map((x) => x.texto).join('; ')}): o servidor registra como inconclusivo.`;
    return null;
  };
  const tom = escolha ? TOM_DO_RESULTADO[escolha] : 'neutro';

  return (
    <div className="cz-form">
      <div className="cz-cartao cz-bloco cz-cartao-forte">
        <h3 className="cz-sobre">RESULTADO DA AUDITORIA · CINCO OPÇÕES</h3>
        <div className="cz-opcoes" role="radiogroup" aria-label="Resultado da auditoria">
          {ORDEM_DOS_RESULTADOS.map((r) => {
            const sel = escolha === r;
            const dica = dicaDo(r);
            const travada = r === 'ERRO_BALCONISTA_CONFIRMADO' && travaDoErro !== null;
            return (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={sel}
                disabled={travada}
                className={`cz-opcao cz-opcao-${TOM_DO_RESULTADO[r]}${sel ? ' cz-opcao-sel' : ''}${travada ? ' cz-opcao-travada' : ''}`}
                onClick={() => setEscolha(r)}
              >
                <span className="cz-radio">
                  {sel && <Icone nome="check" tamanho={12} traco={2.4} />}
                </span>
                <span className="cz-opcao-texto">
                  <b>
                    {TITULO_DA_OPCAO[r]}
                    {(tresEtapas ? etapas?.sugestao.resultado : caso.sugestao?.sugestao) === r && (
                      <i className="cz-sugerido">sugerido</i>
                    )}
                  </b>
                  <span>{EXPLICACAO_DO_RESULTADO[r]}</span>
                  {dica && <small>{dica}</small>}
                </span>
              </button>
            );
          })}
        </div>
        {tresEtapas && etapas ? (
          <p className="cz-pe cz-pe-solto">
            Sugestão do sistema: {etapas.sugestao.texto} A decisão é sua.
          </p>
        ) : (
          caso.sugestao && (
            <p className="cz-pe cz-pe-solto">
              Sugestão do sistema após a recontagem: {caso.sugestao.porque}. A decisão é sua.
            </p>
          )
        )}
      </div>
      {tresEtapas && (
        <div className="cz-cartao cz-bloco">
          <h3>
            Desfecho <span>· opcional</span>
          </h3>
          {pular && (
            <p className="et-aviso-fixo">
              Concluindo <b>sem as etapas</b> que faltam: o motivo abaixo é obrigatório e fica no
              histórico.
            </p>
          )}
          <select
            className="et-select"
            aria-label="Desfecho do caso"
            value={desfecho}
            onChange={(e) => setDesfecho(e.target.value as DesfechoDoCaso | '')}
          >
            <option value="">Não informar</option>
            {(Object.keys(ROTULO_DO_DESFECHO) as DesfechoDoCaso[])
              .filter((d) => d !== 'CORRIGIDO_PELO_BALCONISTA')
              .map((d) => (
                <option key={d} value={d}>
                  {ROTULO_DO_DESFECHO[d]}
                </option>
              ))}
          </select>
        </div>
      )}
      <div className={`cz-cartao cz-motivo-cartao cz-motivo-${tom}`}>
        {escolha && (
          <div className="cz-motivo-topo">
            <Icone
              nome={tom === 'erro' || tom === 'ambar' ? 'warn' : 'check'}
              tamanho={16}
              traco={2}
            />
            <b>Resultado: {TITULO_DA_OPCAO[escolha].toLowerCase()}</b>
          </div>
        )}
        <div className="cz-pad">
          <label className="cz-rotulo">
            MOTIVO <span>· obrigatório, mínimo 5 caracteres</span>
            <textarea
              rows={3}
              maxLength={1000}
              value={motivo}
              placeholder="Ex.: venda de 3 unidades registrada depois do saldo usado no cálculo; a recontagem encontrou 5, sendo 2 em outra exposição."
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="cz-acoes cz-acoes-linha">
            <button
              type="button"
              className="cz-botao cz-botao-primario cz-botao-m cz-flex"
              disabled={!pronto}
              onClick={() =>
                escolha === 'ERRO_BALCONISTA_CONFIRMADO' ? setConfirmando(true) : void enviar()
              }
            >
              {enviando ? 'Concluindo…' : 'Concluir auditoria'}
            </button>
            <button
              type="button"
              className="cz-botao cz-botao-leve cz-botao-m"
              onClick={() => trocarModo('ver')}
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
      {confirmando && (
        <div className="cz-veu" role="presentation" onClick={() => setConfirmando(false)}>
          <div
            className="cz-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cz-modal-titulo"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cz-modal-topo">
              <b id="cz-modal-titulo">
                <Icone nome="warn" tamanho={17} traco={1.7} /> Confirmar erro de contagem
              </b>
              <p>
                Esta conclusão poderá entrar nas métricas de qualidade e nas regras de bloqueio
                configuradas.
              </p>
            </div>
            <div className="cz-modal-corpo">
              <p>
                Caso <b className="cz-mono">{numeroDoCaso(caso.numero)}</b> ·{' '}
                {caso.autoria ?? 'autoria não identificada'} ·{' '}
                {recontou ? 'recontagem registrada' : 'sem recontagem registrada'}
                {caso.fotos.some((f) => f.tipo === 'recontagem') ? ' e foto anexada' : ''}.
              </p>
              {dicaDo('ERRO_BALCONISTA_CONFIRMADO') && (
                <p className="cz-modal-dica">{dicaDo('ERRO_BALCONISTA_CONFIRMADO')}</p>
              )}
              <div className="cz-acoes-linha">
                <button
                  type="button"
                  className="cz-botao cz-botao-primario cz-botao-m cz-flex"
                  onClick={() => void enviar()}
                  autoFocus
                >
                  Confirmar conclusão
                </button>
                <button
                  type="button"
                  className="cz-botao cz-botao-leve cz-botao-m"
                  onClick={() => setConfirmando(false)}
                >
                  Cancelar
                </button>
              </div>
              <p className="cz-pe cz-pe-solto">
                Registro factual: “erro de contagem confirmado pela auditoria”. Nunca julgamento
                sobre a pessoa.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Reabrir (administrador) ---------- */

function Reabertura({ caso, sessao, tratar, carregar, setAviso }: PropsDoCaso) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  if (!caso.config.reaberturaPermitida)
    return (
      <div className="cz-cartao cz-reabrir">
        <b>Reabertura desligada</b>
        <p className="cz-pe cz-pe-solto">
          A reabertura está desligada na configuração da auditoria.
        </p>
      </div>
    );
  const enviar = async () => {
    setEnviando(true);
    setAviso(null);
    const r = await reabrir(sessao, caso.id, { motivo: motivo.trim(), versao: caso.versao });
    setEnviando(false);
    if (!tratar(r, 'A reabertura')) return;
    setMotivo('');
    setAviso({
      tipo: 'ok',
      titulo: 'Caso reaberto',
      texto: 'O caso voltou para a fila. A conclusão anterior permanece no histórico.',
    });
    await carregar();
  };
  return (
    <div className="cz-cartao cz-reabrir">
      <div className="cz-reabrir-topo">
        <b>Reabrir auditoria</b>
        <p>O caso volta para a fila. A conclusão anterior permanece no histórico.</p>
      </div>
      <div className="cz-pad">
        <label className="cz-rotulo">
          MOTIVO <span className="cz-obrig">obrigatório</span>
          <textarea
            rows={2}
            maxLength={1000}
            value={motivo}
            placeholder="Ex.: nova evidência de transferência encontrada…"
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="cz-botao cz-botao-info-contorno cz-botao-m"
          disabled={motivo.trim().length < 5 || enviando}
          onClick={() => void enviar()}
        >
          {enviando ? 'Reabrindo…' : 'Confirmar reabertura'}
        </button>
      </div>
      <p className="cz-pe cz-pe-solto cz-pad-x">
        Se o caso sustentava um bloqueio, o efeito é recalculado pelo servidor — não pela tela.
      </p>
    </div>
  );
}
